import { useState } from "react";
import AdminLayout from "@/layouts/AdminLayout";
import api from "@/services/api";
import { useApi } from "@/hooks/useApi";
import { useNotifications } from "@/context/NotificationContext";

type LeadStatus = "new" | "contacted" | "proposal" | "won" | "lost";

const PIPELINE: LeadStatus[] = ["new", "contacted", "proposal", "won", "lost"];

const statusConfig: Record<LeadStatus, { label: string; color: string; bg: string; border: string }> = {
  new: { label: "New", color: "text-electric-blue", bg: "bg-electric-blue/10", border: "border-electric-blue/30" },
  contacted: { label: "Contacted", color: "text-cyber-orange", bg: "bg-cyber-orange/10", border: "border-cyber-orange/30" },
  proposal: { label: "Proposal", color: "text-cyber-green", bg: "bg-cyber-green/10", border: "border-cyber-green/30" },
  won: { label: "Won", color: "text-cyber-green", bg: "bg-cyber-green/20", border: "border-cyber-green/50" },
  lost: { label: "Lost", color: "text-silver-dim", bg: "bg-dark-border/30", border: "border-dark-border" },
};

interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  service_interest: string | null;
  message: string | null;
  status: LeadStatus;
  assigned_to: string | null;
  converted_client_id: string | null;
  created_at: string;
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function AdminLeads() {
  const { showToast } = useNotifications();
  const [view, setView] = useState<"table" | "kanban">("table");
  const [selected, setSelected] = useState<Lead | null>(null);
  const [convertingLead, setConvertingLead] = useState<Lead | null>(null);

  const { data: leads, loading, refetch } = useApi<Lead[]>("/leads");

  const moveStatus = async (id: string, status: LeadStatus) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      showToast("success", `Lead moved to ${statusConfig[status].label}`);
      refetch();
    } catch {
      showToast("error", "Failed to update lead status");
    }
  };

  const handleConvert = async (data: { plan: string; mrr: number }) => {
    if (!convertingLead) return;
    try {
      await api.post(`/leads/${convertingLead.id}/convert`, data);
      setConvertingLead(null);
      setSelected(null);
      showToast("success", `${convertingLead.name} is now a client!`);
      refetch();
    } catch {
      showToast("error", "Failed to convert lead");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Leads & CRM
            </h1>
            <p className="text-silver text-sm mt-1">
              {leads?.length || 0} total leads
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 p-1 bg-dark-card border border-dark-border rounded-lg">
              {(["table", "kanban"] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`px-3 py-1.5 rounded text-xs font-medium capitalize transition-all cursor-pointer whitespace-nowrap ${
                    view === v ? "bg-electric-blue/20 text-electric-blue" : "text-silver-dim hover:text-white"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
          </div>
        ) : view === "table" ? (
          <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-dark-border">
                    <th className="text-left text-silver-dim font-medium py-3 px-5">Lead</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4 hidden md:table-cell">Service</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4">Status</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4 hidden sm:table-cell">Time</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads?.map((lead) => {
                    const cfg = statusConfig[lead.status];
                    const isConverted = !!lead.converted_client_id;
                    return (
                      <tr
                        key={lead.id}
                        className="border-b border-dark-border/50 hover:bg-dark-panel/50 transition-colors cursor-pointer"
                        onClick={() => setSelected(lead)}
                      >
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-electric-blue/10 border border-electric-blue/20 text-electric-blue text-xs font-bold flex-shrink-0">
                              {lead.name.split(" ").map((n) => n[0]).join("")}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-white font-semibold">{lead.name}</p>
                                {isConverted && (
                                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-cyber-green/15 text-cyber-green border border-cyber-green/30">Client</span>
                                )}
                              </div>
                              <p className="text-silver-dim text-xs">{lead.company || "No company"}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 hidden md:table-cell text-silver text-xs">{lead.service_interest || "—"}</td>
                        <td className="py-4 px-4">
                          <span className={`text-xs px-2.5 py-1 rounded-full border ${cfg.color} ${cfg.bg} ${cfg.border}`}>
                            {cfg.label}
                          </span>
                        </td>
                        <td className="py-4 px-4 hidden sm:table-cell text-silver-dim text-xs">
                          {timeAgo(lead.created_at)}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <select
                              value={lead.status}
                              onChange={(e) => { e.stopPropagation(); moveStatus(lead.id, e.target.value as LeadStatus); }}
                              onClick={(e) => e.stopPropagation()}
                              className="bg-deep-black border border-dark-border rounded-lg px-2 py-1 text-silver text-xs focus:outline-none cursor-pointer"
                            >
                              {PIPELINE.map((s) => (
                                <option key={s} value={s}>{statusConfig[s].label}</option>
                              ))}
                            </select>
                            {lead.status === "won" && !isConverted && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setConvertingLead(lead); }}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-cyber-green/15 border border-cyber-green/30 text-cyber-green text-xs font-semibold hover:bg-cyber-green/25 transition-all cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-user-star-line text-xs" />
                                Convert
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {PIPELINE.map((status) => {
              const cfg = statusConfig[status];
              const colLeads = leads?.filter((l) => l.status === status) || [];
              return (
                <div key={status} className="flex-shrink-0 w-64">
                  <div className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-xl border ${cfg.bg} ${cfg.border}`}>
                    <span className={`text-xs font-bold uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color} font-bold ml-auto`}>
                      {colLeads.length}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {colLeads.map((lead) => {
                      const isConverted = !!lead.converted_client_id;
                      return (
                        <div
                          key={lead.id}
                          onClick={() => setSelected(lead)}
                          className="bg-dark-card border border-dark-border rounded-xl p-4 cursor-pointer hover:border-electric-blue/30 transition-all"
                        >
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="text-white text-xs font-semibold">{lead.name}</p>
                            {isConverted && (
                              <span className="text-xs px-1.5 py-0.5 rounded-full bg-cyber-green/15 text-cyber-green border border-cyber-green/30 flex-shrink-0">Client</span>
                            )}
                          </div>
                          <p className="text-silver-dim text-xs mb-2">{lead.company || "No company"}</p>
                          <p className="text-silver text-xs mb-3">{lead.service_interest || "—"}</p>
                          <div className="flex gap-1 flex-wrap">
                            {status === "won" && !isConverted && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setConvertingLead(lead); }}
                                className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-cyber-green/15 border border-cyber-green/30 text-cyber-green font-semibold hover:bg-cyber-green/25 transition-all cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-user-star-line text-xs" />
                                Convert to Client
                              </button>
                            )}
                            {PIPELINE.filter((s) => s !== status).slice(0, status === "won" ? 1 : 2).map((s) => (
                              <button
                                key={s}
                                onClick={(e) => { e.stopPropagation(); moveStatus(lead.id, s); }}
                                className={`text-xs px-2 py-0.5 rounded border cursor-pointer transition-all ${statusConfig[s].color} ${statusConfig[s].border} hover:opacity-80 whitespace-nowrap`}
                              >
                                → {statusConfig[s].label}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Convert Modal */}
      {convertingLead && (
        <ConvertModal lead={convertingLead} onConfirm={handleConvert} onClose={() => setConvertingLead(null)} />
      )}
    </AdminLayout>
  );
}

function ConvertModal({ lead, onConfirm, onClose }: { lead: Lead; onConfirm: (data: { plan: string; mrr: number }) => void; onClose: () => void }) {
  const [plan, setPlan] = useState<"starter" | "growth" | "enterprise">("growth");
  const [mrr, setMrr] = useState(149);
  const [loading, setLoading] = useState(false);

  const planOptions = [
    { value: "starter", label: "Starter", price: 49, desc: "Up to 1 active project" },
    { value: "growth", label: "Growth", price: 149, desc: "Up to 5 active projects" },
    { value: "enterprise", label: "Enterprise", price: 499, desc: "Unlimited projects & priority support" },
  ] as const;

  const handleConfirm = async () => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 600));
    setLoading(false);
    onConfirm({ plan, mrr });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-dark-panel border border-dark-border rounded-2xl p-6 max-w-md w-full">
        <h3 className="text-white font-bold text-base mb-4">Convert to Client</h3>
        <p className="text-silver text-sm mb-4">{lead.name} from {lead.company || "—"}</p>
        <div className="space-y-2 mb-4">
          {planOptions.map((p) => (
            <button
              key={p.value}
              onClick={() => { setPlan(p.value); setMrr(p.price); }}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all cursor-pointer text-left ${
                plan === p.value ? "border-cyber-green/50 bg-cyber-green/10" : "border-dark-border bg-dark-card"
              }`}
            >
              <div>
                <p className={`text-sm font-semibold ${plan === p.value ? "text-cyber-green" : "text-white"}`}>{p.label}</p>
                <p className="text-silver-dim text-xs">{p.desc}</p>
              </div>
              <p className={`text-sm font-bold ${plan === p.value ? "text-cyber-green" : "text-silver"}`}>${p.price}<span className="text-xs font-normal">/mo</span></p>
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-3 rounded-xl border border-dark-border text-silver hover:text-white transition-colors cursor-pointer text-sm">Cancel</button>
          <button onClick={handleConfirm} disabled={loading} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-cyber-green text-deep-black font-bold text-sm hover:opacity-90 transition-all cursor-pointer disabled:opacity-60">
            {loading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-user-star-line" />}
            {loading ? "Converting..." : "Convert to Client"}
          </button>
        </div>
      </div>
    </div>
  );
}

